import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, ComputedRef, ref, watch } from 'vue';
import { useStore } from 'vuex';
import BigNumber from 'bignumber.js';
import { useI18n } from 'vue-i18n';
import { Token } from '@/sdk/entities/token';
import { compareTokenAddresses } from '@/sdk/utils';
import { BIG_ZERO } from '@/utils/bigNumber';
import { EasyModeWithdrawToken } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-token';
import WithdrawConfirmation from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/withdraw/WithdrawConfirmation.vue';
import { useEasyModeForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeForm';
import { useEasyModeWithdrawCalculation } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeWithdrawCalculation';
import { EasyModeWithdrawForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-form';
import { Portfolio } from '@/sdk/entities/portfolio';
import DialogContent from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/common/DialogContent.vue';
import WithdrawInputPanel from './WithdrawInputPanel.vue';
import WithdrawNoticesList from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/withdraw/WithdrawNoticesList.vue';
import LossInfo from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/LossInfo.vue';
import TokenList from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/tokens-list/TokenList.vue';
import { PortfolioSource } from '@/sdk/entities/PortfolioSource';
import DestinationAddress from '@/components/DestinationAddress.vue';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { useEasyModeWithdraw } from '@/store/modules/portfolios/easy-mode/withdraw/useEasyModeWithdraw';
import NotEnoughCurrencyForFeeNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/NotEnoughCurrencyForFeeNotice.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'EasyModeWithdrawDialog',
  props: {
    portfolio: null
  },
  emits: ["close", "confirmationOpened", "confirm"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'close');
  (e: 'confirmationOpened', value: boolean): void;
  (e: 'confirm'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  portfolio: Portfolio;
}

const { state } = useStore();
const { t } = useI18n();
const { calculateLimits, autoSelectTokens } = useEasyModeWithdrawCalculation();
const {
  forceDisabled,
  selectedTokens: commonSelectedTokens,
  initEasyModeForm,
  getTokenInfoByFormToken,
  easyModeForm: easyModeCommonForm,
  updateTokens,
  getPortfolioAvailableTokens,
  lossUSD,
  crossChainLossUSD,
  needDestinationAddress,
} = useEasyModeForm();
const { setSelectedTokens, withdrawForm } = useEasyModeWithdraw();

const selectedTokens = commonSelectedTokens as ComputedRef<EasyModeWithdrawToken[]>;
const easyModeForm = easyModeCommonForm as EasyModeWithdrawForm;





initEasyModeForm({ portfolio: props.portfolio, action: 'withdraw', baseOrLpTokenAmount: BIG_ZERO });

const destinationAddress = ref(easyModeForm.destinationAddress ?? '');

const withdrawConfirmationOpened = ref(false);

const isCrossChainPortfolio = computed(
  () => props.portfolio.type === PortfolioSource.PORTFOLIO_CROSSCHAIN,
);

const tokensToWithdraw = computed<Token[]>(() => {
  if (forceDisabled.value) {
    return [];
  }

  return selectedTokens.value
    .filter(formToken => formToken.amount?.gt(0))
    .map(
      token =>
        getPortfolioAvailableTokens(props.portfolio).find(tokenInfo =>
          compareTokenAddresses(tokenInfo.tokenAddress, token.address),
        )?.token,
    )
    .filter((token): token is Token => token !== undefined);
});

const openConfirmationModal = () => {
  withdrawConfirmationOpened.value = true;
  emits('confirmationOpened', withdrawConfirmationOpened.value);
};
const closeConfirmationModal = () => {
  withdrawConfirmationOpened.value = false;
  emits('confirmationOpened', withdrawConfirmationOpened.value);
};

const notZeroAmountTokens = computed(() =>
  selectedTokens.value.filter(formToken => formToken.amount?.gt(0)),
);

const withdrawTokens = computed<{ token: Token; amount: BigNumber }[]>(() =>
  notZeroAmountTokens.value.map(formToken => {
    const tokenInfo = getTokenInfoByFormToken(formToken);

    return {
      token: tokenInfo.token,
      amount: formToken.amount!,
    };
  }),
);

const withdrawTokensUSDEquivalent = computed(() => {
  return notZeroAmountTokens.value.reduce((acc, formToken) => {
    const tokenInfo = getTokenInfoByFormToken(formToken);

    return acc.plus(
      formToken.amount!.multipliedBy(
        easyModeForm.portfolio.getTokenPriceInUSD(tokenInfo, 'withdraw'),
      ),
    );
  }, BIG_ZERO);
});

const onChangeDestinationChain = event => {
  easyModeForm.destinationChainId = event.value;

  if (easyModeForm.portfolio.crossChainTokens.length) {
    updateTokens();
    autoSelectTokens();
    calculateLimits();
  }
};

const onClose = () => {
  emits('close');
};

const onConfirm = () => {
  emits('confirm');
};

const sendTransaction = async () => {
  closeConfirmationModal();
  onClose();
  onConfirm();
};

let needAutoTokensSelect = true;

watch(
  () => easyModeForm.lpTokenAmount,
  async () => {
    if (needAutoTokensSelect && easyModeForm.lpTokenAmount.isZero()) {
      return;
    }

    if (needAutoTokensSelect) {
      needAutoTokensSelect = false;
      autoSelectTokens();
    }
    await calculateLimits();
  },
);

watch(
  () => props.portfolio,
  val => (easyModeForm.portfolio = val),
  {
    deep: true,
  },
);

watch(selectedTokens, () => calculateLimits());

watch([needDestinationAddress, destinationAddress], ([needAddress, address]) => {
  if (!ENABLE_FAKE_CARDANO_NETWORK) return;

  easyModeForm.destinationAddress = needAddress && address ? address : null;
  if (!needAddress) {
    destinationAddress.value = '';
  }
});

watch(withdrawTokens, selectedTokens => {
  if (ENABLE_FAKE_CARDANO_NETWORK) {
    setSelectedTokens(selectedTokens);
  } else {
    withdrawForm.selectedTokens = selectedTokens;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(DialogContent, {
      portfolio: __props.portfolio,
      action: "withdraw",
      onCancel: _cache[1] || (_cache[1] = ($event: any) => (emits('close'))),
      onSubmit: openConfirmationModal,
      onChangeNetwork: _cache[2] || (_cache[2] = ($event: any) => (onChangeDestinationChain($event))),
      "chosen-tokens": _unref(tokensToWithdraw),
      "is-notice-loading": false,
      "is-input-active": !_unref(easyModeForm).lpTokenAmount.gt(0),
      "force-disabled": _unref(forceDisabled)
    }, {
      inputBlock: _withCtx(() => [
        _createVNode(WithdrawInputPanel)
      ]),
      destinationAddress: _withCtx(() => [
        (_unref(needDestinationAddress))
          ? (_openBlock(), _createBlock(DestinationAddress, {
              key: 0,
              modelValue: destinationAddress.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((destinationAddress).value = $event)),
              class: "mt-s2"
            }, null, 8, ["modelValue"]))
          : _createCommentVNode("", true)
      ]),
      notice: _withCtx(() => [
        _createVNode(NotEnoughCurrencyForFeeNotice),
        _createVNode(WithdrawNoticesList)
      ]),
      lossInfo: _withCtx(() => [
        _createVNode(LossInfo)
      ]),
      tokenList: _withCtx(() => [
        _createVNode(TokenList, { currentAction: "withdraw" })
      ]),
      _: 1
    }, 8, ["portfolio", "chosen-tokens", "is-input-active", "force-disabled"]),
    _createVNode(_component_Dialog, {
      visible: withdrawConfirmationOpened.value,
      "onUpdate:visible": _cache[3] || (_cache[3] = ($event: any) => ((withdrawConfirmationOpened).value = $event)),
      header: _unref(t)('portfolio.withdraw.confirmDialog.header', { portfolioName: __props.portfolio.name }),
      modal: true,
      "content-class": _unref(state).layout.isMobile ? 'scroll-container' : '',
      dismissableMask: ""
    }, {
      default: _withCtx(() => [
        _createVNode(WithdrawConfirmation, {
          portfolioName: _unref(easyModeForm).portfolio.name,
          impactPercent: _unref(easyModeForm).lossPercent ?? _unref(BIG_ZERO),
          impactUSD: _unref(lossUSD) ?? _unref(BIG_ZERO),
          withdrawTokens: _unref(withdrawTokens),
          withdrawTokensUsdEquivalent: _unref(withdrawTokensUSDEquivalent),
          lpTokens: _unref(easyModeForm).lpTokenAmount,
          "destination-chain": 
        _unref(isCrossChainPortfolio) ? _unref(easyModeForm).destinationChainId?.toString() : null
      ,
          crossChainFeePercent: _unref(easyModeForm).crossChainLossPercent,
          crossChainFeeUSD: _unref(crossChainLossUSD),
          onCancel: closeConfirmationModal,
          onSubmit: sendTransaction
        }, null, 8, ["portfolioName", "impactPercent", "impactUSD", "withdrawTokens", "withdrawTokensUsdEquivalent", "lpTokens", "destination-chain", "crossChainFeePercent", "crossChainFeeUSD"])
      ]),
      _: 1
    }, 8, ["visible", "header", "content-class"])
  ], 64))
}
}

})